<template>
    <StatisticsLayout :title="$t('statistics.mode_x01')" :is-empty="totalGames === 0">
        <template #summary>
            <div class="grid grid-cols-2 gap-x-4">
                <StatsHeroTile
                    :title="$t('statistics.total_games')"
                    :value="totalGames"
                    :footnote="
                        $t('statistics.vs_last_month', {
                            delta: statisticsStore.gamesVsLastMonth('matchtype_501'),
                        })
                    "
                />
                <StatsHeroTile
                    variant="light"
                    :title="$t('statistics.daily_streak')"
                    :value="modeStats.daily_streak?.current ?? 0"
                    :footnote="
                        $t('statistics.highest', { count: modeStats.daily_streak?.highest ?? 0 })
                    "
                />
            </div>
        </template>

        <WinrateCard v-bind="modeStats.winrate" />

        <Panel size="full" padding="p-4" :corners="false">
            <div class="flex flex-col gap-4">
                <div class="flex items-center justify-between gap-4">
                    <h2 class="text-base leading-6 font-black tracking-[0.25px] uppercase">
                        {{ $t('statistics.three_dart_average') }}
                    </h2>
                    <span
                        v-if="allTimeAverage !== null"
                        class="text-bg flex h-6 items-center bg-green-600 px-2 text-xs leading-4 font-black tracking-[0.25px]"
                    >
                        {{ Math.round(allTimeAverage) }}
                    </span>
                </div>

                <div class="flex flex-col gap-2">
                    <div class="relative flex flex-col gap-6">
                        <div
                            v-for="tick in [180, 150, 120, 90, 60, 30, 0]"
                            :key="tick"
                            class="flex h-6 items-center gap-4"
                        >
                            <span
                                class="font-title w-[34px] text-right text-sm leading-[14px] font-extrabold"
                            >
                                {{ tick }}
                            </span>
                            <div class="bg-stroke-2 h-px flex-1"></div>
                        </div>

                        <!-- Plot area: right of the labels, from the 180 line (12px) to the 0 line (300px). -->
                        <svg
                            class="absolute top-0 right-0 left-[50px] h-[312px] w-[calc(100%-50px)] overflow-visible"
                            aria-hidden="true"
                        >
                            <line
                                v-if="allTimeAverage !== null"
                                x1="0"
                                x2="100%"
                                :y1="chartY(allTimeAverage)"
                                :y2="chartY(allTimeAverage)"
                                stroke-width="2"
                                stroke-dasharray="15.3 7.7"
                                class="stroke-green-600"
                            />
                            <line
                                v-for="(point, index) in chartPoints.slice(1)"
                                :key="`line-${index}`"
                                :x1="chartPoints[index].x"
                                :y1="chartPoints[index].y"
                                :x2="point.x"
                                :y2="point.y"
                                class="stroke-titles"
                            />
                            <circle
                                v-for="(point, index) in chartPoints"
                                :key="`point-${index}`"
                                :cx="point.x"
                                :cy="point.y"
                                r="3.5"
                                class="fill-card stroke-titles"
                            />
                        </svg>
                    </div>
                    <span
                        class="font-title self-end text-sm leading-[14px] font-extrabold uppercase"
                    >
                        {{ $t('statistics.last_5_games') }}
                    </span>
                </div>

                <div class="flex flex-col gap-2 text-sm leading-5 tracking-[0.25px]">
                    <div class="flex items-center gap-2">
                        <svg width="16" height="2" aria-hidden="true">
                            <line
                                x1="0"
                                x2="16"
                                y1="1"
                                y2="1"
                                stroke-width="2"
                                stroke-dasharray="4 4"
                                stroke-dashoffset="2"
                                class="stroke-green-600"
                            />
                        </svg>
                        {{ $t('statistics.all_time_average') }}
                    </div>
                    <div class="flex items-center gap-2">
                        <span class="bg-titles h-px w-4"></span>
                        {{ $t('statistics.average_per_game') }}
                    </div>
                </div>
            </div>
        </Panel>

        <Panel size="full" padding="p-4" :corners="false">
            <h2 class="text-base leading-6 font-black tracking-[0.25px] uppercase">
                {{ $t('statistics.other_statistics') }}
            </h2>
            <StatsList :rows="otherStatistics" class="mt-4" />
        </Panel>
        <PersonalRecords :records="records" />
    </StatisticsLayout>
</template>

<script>
import { mapStores } from 'pinia'
import { useStatisticsStore } from '@/stores/statistics.js'
import StatisticsLayout from '@/components/statistics/StatisticsLayout.vue'
import StatsHeroTile from '@/components/statistics/StatsHeroTile.vue'
import Panel from '@/components/layout/Panel.vue'
import StatsList from '@/components/statistics/StatsList.vue'
import WinrateCard from '@/components/statistics/WinrateCard.vue'
import PersonalRecords from '@/components/statistics/PersonalRecords.vue'

export default {
    name: 'X01StatsView',
    components: {
        StatisticsLayout,
        StatsHeroTile,
        Panel,
        StatsList,
        WinrateCard,
        PersonalRecords,
    },
    computed: {
        records() {
            const checkIn = {
                single: 'match_settings.straight_in',
                double: 'match_settings.double_in',
                master: 'match_settings.master_in',
            }
            const checkOut = {
                single: 'match_settings.straight_out',
                double: 'match_settings.double_out',
                master: 'match_settings.master_out',
            }
            return (this.statisticsStore.allTime?.mode_stats?.matchtype_501?.records ?? []).map(
                (record) => ({
                    title: this.$t('statistics.record_average', {
                        value: Math.round(record.value),
                    }),
                    details: [
                        this.$t(checkIn[record.check_in] ?? checkIn.single),
                        this.$t(checkOut[record.check_out] ?? checkOut.double),
                    ],
                    playedAt: record.played_at,
                }),
            )
        },

        ...mapStores(useStatisticsStore),

        totalGames() {
            return this.statisticsStore.totalGames('matchtype_501')
        },

        modeStats() {
            return this.statisticsStore.allTime?.mode_stats?.matchtype_501 ?? {}
        },

        allTimeAverage() {
            return (
                this.statisticsStore.allTime?.aggregated_stats?.three_dart_avg?.matchtype_501
                    ?.average ?? null
            )
        },

        /** The last games' averages, spread evenly over the width, oldest on the left. */
        chartPoints() {
            const averages = this.modeStats.recent_averages ?? []
            return averages.map((average, index) => ({
                x: `${(index / Math.max(averages.length - 1, 1)) * 100}%`,
                y: this.chartY(average),
            }))
        },

        otherStatistics() {
            const stats = this.statisticsStore.allTime?.aggregated_stats
            const round = (value) => (value == null ? '-' : String(Math.round(value * 10) / 10))
            const percentage = (value) => (value == null ? '-' : `${Math.round(value)}%`)

            return [
                {
                    label: this.$t('statistics.avg_first_9'),
                    value: round(stats?.avg_first_nine_darts?.matchtype_501),
                },
                {
                    label: this.$t('statistics.avg_darts_per_leg'),
                    value: round(stats?.avg_darts_per_leg?.matchtype_501),
                },
                {
                    label: this.$t('statistics.checkout_rate'),
                    value: percentage(this.modeStats.checkout_rate),
                },
                {
                    label: this.$t('statistics.t20_hit_rate'),
                    value: percentage(this.modeStats.t20_hit_rate),
                },
            ]
        },
    },
    methods: {
        /** Grid lines are 48px apart: 180 sits at 12px and 0 at 300px. */
        chartY(average) {
            return 12 + (180 - Math.min(Math.max(average, 0), 180)) * 1.6
        },
    },
}
</script>
